import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import api from "@/api";
import { useAuth } from "@/context/AuthContext";
import { ChevronLeft } from "lucide-react";

function fmt(n) { if (n == null) return "—"; return new Intl.NumberFormat().format(n); }
function pct(part, total) {
  if (!total) return "—";
  return `${Math.round((part / total) * 100)}%`;
}
function daysAgo(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  const now = new Date();
  return Math.floor((now - d) / (1000 * 60 * 60 * 24));
}
function relDate(dateStr) {
  const d = daysAgo(dateStr);
  if (d == null) return "—";
  if (d === 0) return "today";
  if (d === 1) return "yesterday";
  if (d < 7) return `${d}d ago`;
  return dateStr;
}

export default function Admin() {
  const { user, loading } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [tab, setTab] = useState("users");
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!user || user.role !== "admin") return;
    (async () => {
      try {
        const [s, u, ss] = await Promise.all([
          api.get("/admin/stats"),
          api.get("/admin/users?limit=200"),
          api.get("/admin/sessions?limit=100"),
        ]);
        setStats(s.data);
        setUsers(u.data.users);
        setSessions(ss.data.sessions);
      } catch (e) {
        setErr(e.response?.data?.detail || "Failed to load");
      }
    })();
  }, [user]);

  if (loading) return <div className="min-h-screen bg-app flex items-center justify-center text-t3 text-xs tracking-widest">CHARGE1</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/dashboard" replace />;

  return (
    <div className="min-h-screen bg-app max-w-5xl mx-auto px-5 pt-10 pb-8">
      <div className="flex items-center justify-between">
        <Link to="/dashboard" className="stepper" data-testid="admin-back"><ChevronLeft size={18} /></Link>
        <div className="text-center">
          <div className="overline">Charge1</div>
          <h1 className="font-display text-2xl uppercase mt-1">Admin</h1>
        </div>
        <div style={{ width: 44 }} />
      </div>

      {err && <div className="mt-6 text-red text-sm" data-testid="admin-error">{err}</div>}

      {/* Stats grid */}
      <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="admin-stats">
        <Stat k="Total users" v={fmt(stats?.totalUsers)} sub={`${stats?.authSplit?.emailPassword || 0} email · ${stats?.authSplit?.google || 0} Google`} />
        <Stat k="Signups · 7d" v={fmt(stats?.signups7d)} sub={`${fmt(stats?.signups30d)} in 30d`} />
        <Stat k="Active · 7d" v={fmt(stats?.active7d)} sub={`${pct(stats?.active7d, stats?.totalUsers)} of users`} />
        <Stat k="Active · 30d" v={fmt(stats?.active30d)} sub={`${pct(stats?.active30d, stats?.totalUsers)} of users`} />
        <Stat k="Sessions · total" v={fmt(stats?.totalSessions)} />
        <Stat k="Sessions · 7d" v={fmt(stats?.sessions7d)} />
        <Stat k="Google users" v={fmt(stats?.authSplit?.google)} sub={pct(stats?.authSplit?.google, stats?.totalUsers)} />
        <Stat k="Email users" v={fmt(stats?.authSplit?.emailPassword)} sub={pct(stats?.authSplit?.emailPassword, stats?.totalUsers)} />
      </div>

      {/* Tabs */}
      <div className="mt-8 flex gap-2">
        <button className={`chip ${tab === "users" ? "selected" : ""}`} onClick={() => setTab("users")} data-testid="admin-tab-users">Users ({users.length})</button>
        <button className={`chip ${tab === "sessions" ? "selected" : ""}`} onClick={() => setTab("sessions")} data-testid="admin-tab-sessions">Recent sessions ({sessions.length})</button>
      </div>

      {tab === "users" && (
        <div className="mt-4 card overflow-hidden" data-testid="admin-users-table">
          <div className="grid grid-cols-12 border-hair-b py-2 px-4 text-t3 text-xs uppercase tracking-widest">
            <div className="col-span-4">User</div>
            <div className="col-span-2">Auth</div>
            <div className="col-span-2">Signed up</div>
            <div className="col-span-2">Last session</div>
            <div className="col-span-1 text-right">Sess.</div>
            <div className="col-span-1 text-right">Days</div>
          </div>
          {users.map((u) => (
            <div key={u.user_id} className="grid grid-cols-12 border-hair-b py-3 px-4 text-sm items-center hover:bg-elev transition-colors">
              <div className="col-span-4 min-w-0">
                <div className="text-t1 truncate">{u.name || "—"}</div>
                <div className="text-t3 text-xs truncate">{u.email}</div>
              </div>
              <div className="col-span-2">
                <span className={`chip py-1 px-2 text-xs ${u.authMethod === "google" ? "" : ""}`}>{u.authMethod}</span>
                {u.role === "admin" && <span className="ml-1 text-xs text-lime">admin</span>}
              </div>
              <div className="col-span-2 text-t2 tabular font-mono text-xs">{relDate(u.created_at?.slice(0, 10))}</div>
              <div className="col-span-2 text-t2 tabular font-mono text-xs">{relDate(u.lastSessionDate)}</div>
              <div className="col-span-1 text-right tabular font-mono">{u.sessionsCount}</div>
              <div className="col-span-1 text-right text-t3 text-xs">{u.daysPerWeek || "—"}</div>
            </div>
          ))}
          {users.length === 0 && <div className="p-8 text-center text-t3">No users yet.</div>}
        </div>
      )}

      {tab === "sessions" && (
        <div className="mt-4 card overflow-hidden" data-testid="admin-sessions-table">
          <div className="grid grid-cols-12 border-hair-b py-2 px-4 text-t3 text-xs uppercase tracking-widest">
            <div className="col-span-4">User</div>
            <div className="col-span-2">Date</div>
            <div className="col-span-2">Focus</div>
            <div className="col-span-1 text-right">Sets</div>
            <div className="col-span-2 text-right">Volume</div>
            <div className="col-span-1 text-right">Min</div>
          </div>
          {sessions.map((s, i) => (
            <div key={`${s.date}-${s.time}-${s.user_id || 'anon'}-${i}`} className="grid grid-cols-12 border-hair-b py-3 px-4 text-sm items-center hover:bg-elev transition-colors">
              <div className="col-span-4 min-w-0">
                <div className="text-t1 truncate">{s.userName || "—"}</div>
                <div className="text-t3 text-xs truncate">{s.userEmail}</div>
              </div>
              <div className="col-span-2 text-t2 tabular font-mono text-xs">{s.date} {s.time}</div>
              <div className="col-span-2 uppercase text-t1">{s.focus}</div>
              <div className="col-span-1 text-right tabular font-mono">{s.totalSets ?? "—"}</div>
              <div className="col-span-2 text-right tabular font-mono text-lime">{s.volumeKg ? `${Math.round(s.volumeKg)} kg` : "—"}</div>
              <div className="col-span-1 text-right text-t3">{s.durationSec ? Math.round(s.durationSec / 60) : "—"}</div>
            </div>
          ))}
          {sessions.length === 0 && <div className="p-8 text-center text-t3">No sessions yet.</div>}
        </div>
      )}
    </div>
  );
}

function Stat({ k, v, sub }) {
  return (
    <div className="card p-3">
      <div className="overline">{k}</div>
      <div className="font-display text-3xl mt-1 tabular">{v}</div>
      {sub && <div className="text-t3 text-xs mt-1 truncate">{sub}</div>}
    </div>
  );
}
